<!-- Copyright 2025, Common Good Learning Tools LLC -->
<template>
	<k-dialog v-if="local_asset" v-model="asset_loaded_locally" max-width="500px" class="k-payment-window" persistent>
		<k-card>
			<k-card-title>
				<div>
					<div>Payments for:</div>
					<b>{{ local_asset.asset_title }}</b>
				</div>
			</k-card-title>

			<k-card-text style="color: #000;">
				<div class="k-payment-section author">
					<div class="d-flex">
						<div>Author: {{ local_asset.author_name }}</div>

						<k-spacer/>

						<k-btn v-if="!local_asset.payment_complete" color="secondary" x-small @click="add_payment_init('author')">Add Payment</k-btn>
					</div>
					
					<k-data-table
						v-if="local_asset.author_payments"
						:items="local_asset.author_payments"
						:headers="headers"
						hide-default-footer
						dense
					>
						<template v-slot:item="{ item, index }">
							<tr class="k-table-row" >
								<td v-for="(header, i) in headers" :key="'header_' + i" :class="{'center-text': header.align == 'center'}">
									<k-icon v-if="header.value == 'remove'" class="button ml-4" color="red" small @click="remove_payment('author', index)">fas fa-remove</k-icon>

									<span v-else v-html="display_value(item, header.value)"></span>
								</td>
							</tr>
						</template>
					</k-data-table>

					<k-divider class="my-4"/>
				</div>

				<div v-for="(reviewer, index) in local_asset.reviewer_data" :key="'reviewer_' + index">
					<div class="d-flex">
						<div>Reviewer: {{ reviewer.user_name }}</div>
						<k-icon v-if="reviewer.approved_date" class="ml-2" color="green" small>fas fa-check-square</k-icon>

						<k-spacer/>

						<k-btn v-if="!local_asset.payment_complete" color="secondary" x-small @click="add_payment_init('reviewer', reviewer)">Add Payment</k-btn>
					</div>

					<k-data-table
						v-if="reviewer.payments"
						:items="reviewer.payments"
						:headers="headers"
						hide-default-footer
						dense
					>
						<template v-slot:item="{ item, index }">
							<tr class="k-table-row" >
								<td v-for="(header, i) in headers" :key="'header_' + i" :class="{'center-text': header.align == 'center'}">
									<k-icon v-if="header.value == 'remove'" class="button ml-4" color="red" small @click="remove_payment(reviewer, index)">fas fa-remove</k-icon>

									<span v-else v-html="display_value(item, header.value)"></span>
								</td>
							</tr>
						</template>
					</k-data-table>

					<k-divider class="my-4"/>
				</div>

				<k-btn v-if="!local_asset.payment_complete" color="tertiary" class="mt-2" small @click="complete_payment(true)">Mark as Complete</k-btn>
				<k-btn v-else color="tertiary" class="mt-2" small @click="complete_payment(false)">Mark as Incomplete</k-btn>
			</k-card-text>

			<k-card-actions class="pl-4 pr-4 pb-4">
				<k-spacer/>
				<k-btn color="secondary" class="mr-1" @click="$emit('done')">
					Done
				</k-btn>
			</k-card-actions>
		</k-card>

		<k-dialog v-if="adding_payment" v-model="adding_payment" max-width="350px" persistent>
			<k-card>
				<k-card-title>
					<div>Add Payment For:</div>
					<div>{{ adding_payment.user.user_name }}</div>
				</k-card-title>

				<k-card-text style="color: #000;">
					<k-text-field
						v-model="adding_payment.payment_data.amount"
						class="mb-6"
						label="Amount"
						prefix="$"
						type="number"
						autofocus
					/>

					<k-menu transition="scale-transition" offset-y close-on-content-click>
						<template v-slot:activator="{ on }">
							<k-text-field
								v-on="on"
								label="Date"
								:value="date_format(adding_payment.payment_data.date)"
								prepend-inner-icon="fas fa-calendar-week"
								background-color="#f8f8f8"
								outlined dense hide-details readonly
							/>
						</template>

						<k-date-picker v-model="adding_payment.payment_data.date" no-title scrollable/>
					</k-menu>

					<k-text-field
						v-model="adding_payment.payment_data.note"
						label="Note"
					/>
				</k-card-text>

				<k-card-actions class="pl-4 pr-4 pb-4">
					<k-spacer/>
					<k-btn color="secondary" class="mr-1" @click="adding_payment = null">
						<k-icon small class="mr-2">fas fa-times</k-icon>
						Cancel
					</k-btn>

					<k-btn color="primary" class="k-tight-btn" @click="add_payment">
						<k-icon small class="mr-2">fas fa-check</k-icon>
						Save
					</k-btn>
				</k-card-actions>
			</k-card>
		</k-dialog>
	</k-dialog>
</template>

<script>
import { mapState } from 'vuex'

export default {
	name: 'AssetPaymentDialog',

	props: {
		selected_asset: {type: Object, required: true},
	},

	data() {
		return {
			local_asset: null,
			adding_payment: null,
			headers: [
				{ text: 'Amount', align: 'start', sortable: false, width: '25px', value: 'amount'},
				{ text: 'Date', align: 'start', sortable: false, width: '50px', value: 'date'},
				{ text: 'Note', align: 'start', sortable: false, value: 'note'},
				{ text: 'Remove', align: 'center', sortable: false, width: '20px', value: 'remove'},
			],
		}
	},

	computed: {
		...mapState(['user_info', 'site_config']),

		asset_loaded_locally() {
			return !! this.local_asset
		},
	},

	mounted() {
		this.local_asset = object_copy(this.selected_asset)
	},

	methods: {
		display_value(item, key) {
			let value = item[key]
			
			if (key.includes('payments')) {
				if (!empty(value)){
					let amount = 0

					value.forEach(x => { amount += parseInt(x.amount)})

					value = '$' + amount
				} else {
					value = '$0'
				}
			} else if (key.includes('date') || key.includes('_at')) {
				if (value) {
					value = date.format(new Date(value), 'M/D/YYYY')
				} else {
					value = '-'
				}
			} else if (key == 'asset_title') {
				let icon = U.directive_type_icon(item.asset_type)
				value = `<i class="mr-1 fas ${icon}"></i>${value}`
			} else if (key == 'amount') {
				value = '$' + value
			}

			return value
		},

		date_format(date_to_format) {
			if (date_to_format) {
				return date.format(new Date(date_to_format), 'M/D/YYYY', (new Date()).getTimezoneOffset())
			} else {
				return '-'
			}
		},

		complete_payment(complete) {
			let asset = object_copy(this.local_asset)

			asset.payment_complete_date = complete ? date.format(U.today(), 'YYYY-MM-DD') : null
			asset.payment_complete = complete ? 1 : 0

			this.save(asset, complete)
		},

		add_payment_init(role, user) {
			if (!user) user = {
				user_id: this.local_asset.author_id,
				user_name: this.local_asset.author_name,
			}

			this.adding_payment = {
				role,
				user,
				payment_data: {
					amount: null,
					date: null,
					note: null
				}
			}
		},

		add_payment() {
			let payment_data = object_copy(this.adding_payment.payment_data)
			let asset = object_copy(this.local_asset)

			if (this.adding_payment.role == 'author') {
				if (!asset.author_payments) {
					asset.author_payments = []
				}
				asset.author_payments.push(payment_data)
			} else {
				asset.reviewer_data = (asset.reviewer_data || []).map(reviewer => {
					if (reviewer.user_id == this.adding_payment.user.user_id) {
						if (!reviewer.payments) {
							reviewer.payments = []
						}
						reviewer.payments.push(payment_data)
					}

					return reviewer
				})
			}

			this.adding_payment = null
			this.save(asset)
		},

		remove_payment(user, index) {
			this.$confirm({
				title: 'Are you sure?',
				text: 'Are you sure you want to remove this payment record?',
				acceptText: 'Remove Record',
				acceptColor: 'red',
				dialogMaxWidth: 600
			}).then(() => {
				let asset = object_copy(this.local_asset)
				if (user == 'author') {
					asset.author_payments.splice(index, 1)
					if (!asset.author_payments.length) asset.author_payments = '*CLEAR*'
				} else {
					asset.reviewer_data = asset.reviewer_data.map(reviewer => {
						if (reviewer.user_id == user.user_id) {
							reviewer.payments.splice(index, 1)
						}

						return reviewer
					})
				}
				
				this.save(asset)
			}).catch(() => {})
		},

		save(asset, close) {
			if (!asset.publish_date) asset.publish_date = '*CLEAR*'
			if (!asset.submitted_date) asset.submitted_date = '*CLEAR*'
			let payload = {user_id: this.user_info.user_id, payment_data: asset}

			this.$store.dispatch('save_asset_review_payment', payload)
				.then((result) => {
					this.local_asset = result.payment_data
					this.$emit('updated', result.payment_data, close)
				})
		}
	},
}
</script>

<style lang="scss" scoped></style>